import React, { useEffect, useMemo, useState } from 'react';
import { Box } from '@chakra-ui/react';
import Highcharts from 'highcharts';
import highchartsAccessibility from 'highcharts/modules/accessibility';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { capitalizeFirst } from 'clipsal-cortex-utils/src/formatting/formatting';

import '../activity.scss';

import { SwitchOption } from 'clipsal-cortex-ui/src/components/MultiToggleSwitch';

import { DateRangeType, DateRangeTypePicker } from '../../../common/components/DateRangeTypePicker';
import { SlidingAnimationPageBase } from '../../../common/components/SlidingAnimationPageBase';
import { useSiteCapabilityCheck } from '../../../common/hooks/use-site-capability-check';
import { useGetDevicesQuery } from '../../devices/devicesApi';
import { useGetSavingsQuery } from '../../savings/savingsApi';
import { useGetCostsQuery } from '../../site/costApi';
import { selectCurrentDayForSite } from '../../site/siteSlice';
import { useGetUsageWithCostsQuery } from '../activityApi';
import { ActivitySummaryCards } from '../default/ActivitySummaryCards';
import { BatterySOCChart } from '../default/BatterySOCChart';
import { ChartSwitcher } from '../default/ChartSwitcher';
import { ConsumptionSelect } from '../default/ConsumptionSelect';
import { FilterByDevice } from '../default/FilterByDevice';
import { PowerSourceSelect } from '../default/PowerSourceSelect';
import { useInitialSelectedPowerSources } from '../default/use-initial-selected-power-sources';
import { useSource } from '../default/use-source';
import { ActivityDataSource, PowerSourceConfiguration } from '../types';
import { getInitialSelectedDates } from '../utils';
import { ActivityDatePicker } from './ActivityDatePicker';
import { useDateRangeOptions } from './use-date-range-options';

highchartsAccessibility(Highcharts);

export interface ActivityState {
  selectedDateRangeType: DateRangeType;
  rangeTypeToSelectedPeriod: Record<DateRangeType, Date>;
  selectedPowerSources: PowerSourceConfiguration;
  selectedDeviceId: number | null;
  selectedDeviceAssignment?: string;
  isSelectedDeviceDisplayedInChart: boolean;
  isSelectedConsumptionInChart: boolean;
  isCostViewSelected: boolean;
}

interface Props {
  options?: {
    powerSources: PowerSourceConfiguration;
    dateRange: SwitchOption<DateRangeType>[];
  };
}

export function ActivityView({ options }: Props) {
  const { t } = useTranslation();
  const source = useSource();
  const { siteHasMeter, siteHasInverterSolar, siteHasMeterSolar } = useSiteCapabilityCheck();
  const currentDay = useSelector(selectCurrentDayForSite);
  const { data: devices, isError: isDevicesError } = useGetDevicesQuery();
  const DATE_RANGE_OPTIONS = useDateRangeOptions();
  const initialSelectedPowerSources = useInitialSelectedPowerSources();

  const [state, setState] = useState<ActivityState>({
    selectedDateRangeType: DateRangeType.Day,
    rangeTypeToSelectedPeriod: getInitialSelectedDates(currentDay),
    selectedPowerSources: initialSelectedPowerSources,
    selectedDeviceId: devices[0]?.appliance_id ?? 0,
    selectedDeviceAssignment: devices[0]?.assignment,
    isSelectedDeviceDisplayedInChart: false,
    isSelectedConsumptionInChart: true,
    isCostViewSelected: false,
  });
  const {
    selectedDateRangeType,
    rangeTypeToSelectedPeriod,
    selectedPowerSources,
    isSelectedDeviceDisplayedInChart,
    isSelectedConsumptionInChart,
    selectedDeviceId,
    isCostViewSelected,
  } = state;

  const {
    data: usageData,
    isLoading: isUsageLoading,
    isFetching: isUsageFetching,
    isError: isUsageError,
  } = useGetUsageWithCostsQuery(rangeTypeToSelectedPeriod[selectedDateRangeType], selectedDateRangeType, false, false);
  const { energyUsageData, powerUsageData, displayedDevices, energyIndependencePercentage } = usageData;

  const {
    data: costsData,
    isLoading: isCostsLoading,
    isFetching: isCostsFetching,
    isError: isCostsError,
  } = useGetCostsQuery(rangeTypeToSelectedPeriod[selectedDateRangeType], selectedDateRangeType);
  const { costData } = costsData;

  const { data: savingsData, isLoading: isSavingsLoading } = useGetSavingsQuery(
    rangeTypeToSelectedPeriod[selectedDateRangeType],
    selectedDateRangeType
  );

  const isError = isDevicesError || isUsageError || isCostsError;
  const isLoaded = !isUsageFetching && !isUsageLoading && !isCostsFetching && !isCostsLoading && !isSavingsLoading;

  const title = useMemo(
    () =>
      source ? t(`${capitalizeFirst(source.toLowerCase())}.${source.toLowerCase()} activity`) : t('Activity.activity'),
    [source, t]
  );

  useEffect(() => {
    // We use a separate `isLoaded` value to RTK query to ensure we select a device after the data has loaded.
    // Whenever the displayed devices change, we want to ensure that the selected device is retained. Otherwise,
    // we update the selected device to the first one in the list.
    if (isLoaded) {
      setState((prevState) => {
        const prevSelectedDevice = displayedDevices.find(
          (device) => device.appliance_id === prevState.selectedDeviceId
        );
        const selectedDevice = prevSelectedDevice ?? displayedDevices[0];

        return {
          ...prevState,
          selectedDeviceId: selectedDevice?.appliance_id ?? 0,
          selectedDeviceAssignment: selectedDevice?.assignment,
        };
      });
    }
  }, [displayedDevices, isLoaded]);

  async function handleChangeSelectedDate(newDate: Date | null) {
    setState((prevState) => ({
      ...prevState,
      rangeTypeToSelectedPeriod: {
        ...rangeTypeToSelectedPeriod,
        [selectedDateRangeType]: newDate,
      },
    }));
  }

  async function handleChangeSelectedDateRangeType(newDateRangeType: DateRangeType) {
    // Ensure cost chart is only available on aggregate date range types
    setState((prevState) => ({
      ...prevState,
      selectedDateRangeType: newDateRangeType,
      isCostViewSelected: newDateRangeType === DateRangeType.Day ? false : prevState.isCostViewSelected,
    }));
  }

  const handleSelectChart = (isCostViewSelected: boolean) => {
    setState((prevState) => ({ ...prevState, isCostViewSelected }));
  };

  const handleSelectedPowerSources = (property: ActivityDataSource, value: boolean) => {
    setState((prevState) => ({ ...prevState, selectedPowerSources: { ...selectedPowerSources, [property]: value } }));
  };

  const handleSelectDevice = (selectedDeviceId: number, selectedDeviceAssignment: string) => {
    setState((prevState) => ({ ...prevState, selectedDeviceId, selectedDeviceAssignment }));
  };

  const handleToggleDeviceDisplay = () => {
    setState((prevState) => ({ ...prevState, isSelectedDeviceDisplayedInChart: !isSelectedDeviceDisplayedInChart }));
  };

  const handleToggleConsumption = () => {
    setState((prevState) => ({ ...prevState, isSelectedConsumptionInChart: !isSelectedConsumptionInChart }));
  };

  return (
    <SlidingAnimationPageBase
      includeBottomNav={!source}
      includeTopNavHamButton={!source}
      backURL={source ? `../${source}/home?direction=back` : undefined}
      title={title}
      includeSchneiderHamButton={!source}
    >
      <Box px={3}>
        {source !== 'battery' && (
          <DateRangeTypePicker
            dateRangeOptions={options?.dateRange ?? DATE_RANGE_OPTIONS}
            selectedDateRangeType={selectedDateRangeType}
            setSelectedDateRangeType={handleChangeSelectedDateRangeType}
          />
        )}
        <ActivityDatePicker
          isLoaded={isLoaded}
          selectedDateRangeType={selectedDateRangeType}
          rangeTypeToSelectedPeriod={rangeTypeToSelectedPeriod}
          onChangeDate={handleChangeSelectedDate}
        />

        <ActivitySummaryCards
          isLoaded={isLoaded}
          costData={costData}
          savingsData={savingsData}
          energyUsageData={energyUsageData}
          powerUsageData={powerUsageData}
          isCostViewSelected={isCostViewSelected}
          energyIndependencePercentage={energyIndependencePercentage}
          selectedDateRangeType={selectedDateRangeType}
        />

        <ChartSwitcher
          state={state}
          isLoaded={isLoaded}
          isError={isError}
          costData={costData}
          savingsData={savingsData}
          energyUsageData={energyUsageData}
          powerUsageData={powerUsageData}
          onChartChange={handleSelectChart}
        />

        {/* Display the SOC chart when in day view for battery activity */}
        {selectedDateRangeType === DateRangeType.Day && source === 'battery' && !!powerUsageData.length && (
          <BatterySOCChart
            isLoaded={isLoaded}
            powerUsageData={powerUsageData}
            selectedDate={rangeTypeToSelectedPeriod[selectedDateRangeType]}
          />
        )}
      </Box>

      <Box id="filters-list">
        {!isCostViewSelected && !source && (
          <PowerSourceSelect
            powerSources={
              options?.powerSources ?? {
                solar: siteHasInverterSolar || siteHasMeterSolar,
                grid: true,
                battery: selectedDateRangeType === DateRangeType.Day,
              }
            }
            selectedDateRangeType={selectedDateRangeType}
            selectedPowerSources={selectedPowerSources}
            onSetSelectedPowerSources={handleSelectedPowerSources}
          />
        )}

        {!source && (
          <ConsumptionSelect
            isSelectedConsumptionInChart={isSelectedConsumptionInChart}
            onToggleConsumption={handleToggleConsumption}
          />
        )}

        {/* Only show appliance-level data if the site has a meter */}
        {siteHasMeter && !source && !isError && (
          <FilterByDevice
            displayedDevices={displayedDevices}
            selectedDateRangeType={selectedDateRangeType}
            selectedDeviceId={selectedDeviceId}
            isLoaded={isLoaded}
            onSelectDevice={handleSelectDevice}
            isSelectedDeviceDisplayedInChart={isSelectedDeviceDisplayedInChart}
            onToggleDeviceDisplay={handleToggleDeviceDisplay}
          />
        )}
      </Box>
    </SlidingAnimationPageBase>
  );
}
